import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney, formatAbbrev, parseAbbrev } from "@/lib/format";
import { composedGainFromPrices, composedGainForStock, formatPctInfo } from "@/lib/price";
import ScoreDial from "@/components/ScoreDial";
import DecisionBadge from "@/components/DecisionBadge";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2, Save } from "lucide-react";

// -----------------------------------------------------------------------------
// Price + informational % row — used for Entry / Exit / Close prices.
// -----------------------------------------------------------------------------
// Price + informative % row — user types both. The % is NEVER computed by the
// app (it's just what Webull displayed at capture time, recopied by hand).
// -----------------------------------------------------------------------------
function PriceRow({ title, hint, priceValue, pctValue, testIdPrefix, onSave, readOnly }) {
  const [p, setP] = useState(priceValue ?? "");
  const [pct, setPct] = useState(pctValue ?? "");
  useEffect(() => setP(priceValue ?? ""), [priceValue]);
  useEffect(() => setPct(pctValue ?? ""), [pctValue]);

  const asNum = p === "" || p == null ? null : parseAbbrev(p);
  const pctNum = pct === "" || pct == null ? null : parseAbbrev(pct);

  const handleSave = () => onSave({ price: asNum, pct: pctNum });

  const pctColor = pctNum == null ? "text-muted-2" : pctNum >= 0 ? "text-invest" : "text-ignore";

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_120px_auto] gap-3 items-center bg-input border border-line rounded-lg p-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-2">{title}</div>
        <div className="text-xs text-muted-1 mt-0.5">{hint}</div>
      </div>
      <Input
        value={p}
        onChange={(e) => setP(e.target.value)}
        placeholder="ex : 0.1024"
        disabled={readOnly}
        className={`h-9 bg-card-1 border-line-strong font-mono-num text-right ${readOnly ? "opacity-60" : ""}`}
        data-testid={`${testIdPrefix}-price-input`}
        inputMode="decimal"
      />
      <Input
        value={pct}
        onChange={(e) => setPct(e.target.value)}
        placeholder="% Webull"
        className={`h-9 bg-card-1 border-line-strong font-mono-num text-right ${pctColor}`}
        data-testid={`${testIdPrefix}-pct-input`}
        inputMode="decimal"
      />
      {!readOnly ? (
        <Button
          onClick={handleSave}
          className="h-9 btn-primary text-xs"
          data-testid={`${testIdPrefix}-price-save`}
        ><Save size={12} className="mr-1" /> OK</Button>
      ) : <div />}
    </div>
  );
}

export default function StockDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [amountDraft, setAmountDraft] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/stocks/${id}`);
      setS(r.data);
      setNotes(r.data.notes || "");
      setAmountDraft(r.data.entry_amount != null ? String(r.data.entry_amount) : "");
    } catch (e) {
      toast.error("Introuvable");
      nav("/");
    }
  };
  useEffect(() => { load(); }, [id]);

  const patch = async (payload, successMsg) => {
    try {
      const r = await api.patch(`/stocks/${id}`, payload);
      setS(r.data);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  const toggleStatus = async (checked) => {
    // checked === true → Real
    await patch({ status: checked ? "real" : "test" }, checked ? "Basculé en Réel" : "Basculé en Test");
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const r = await api.patch(`/stocks/${id}`, { notes });
      setS(r.data);
      toast.success("Note enregistrée");
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setSavingNotes(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/stocks/${id}`);
      toast.success("Action supprimée");
      nav("/");
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  const saveAmount = async () => {
    const val = amountDraft === "" ? null : parseAbbrev(amountDraft);
    await patch({ entry_amount: val }, val == null ? "Montant remis en auto" : "Montant investi mis à jour");
  };

  if (!s) return <div className="text-muted-1">Chargement…</div>;

  const isReal = (s.status || "test").toLowerCase() === "real";
  const br = s.score_breakdown;
  const prevClose = s.criteria?.prev_close;

  // Live gain preview — price-based only per new spec.
  // Legacy % (entry.percent / exit.percent / close.percent) is now the
  // "informative % from Webull", not a gain input.
  const gainPrices = composedGainForStock(s);

  return (
    <div className="space-y-6 sm:space-y-8">
      <button
        onClick={() => nav(-1)}
        className="text-xs text-muted-1 hover:text-app transition-colors flex items-center gap-1.5"
        data-testid="stock-back-btn"
      ><ArrowLeft size={14} /> retour</button>

      {/* Hero */}
      <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center bg-card-1 border border-line rounded-xl p-5 sm:p-8">
        <ScoreDial score={s.score} />
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {s.rank_in_list != null && (
              <span className="text-[10px] font-mono-num uppercase tracking-widest text-muted-2 border border-line-strong rounded-full px-2 py-0.5">
                Rang {s.rank_in_list}
              </span>
            )}
            <DecisionBadge score={s.score} size="lg" />
            <StatusBadge status={s.status} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-app" data-testid="stock-symbol">{s.symbol}</h1>
          <div className="text-muted-1 text-sm">{s.name || " "}</div>
          <div className="mt-3 text-xs font-mono-num text-muted-2">Journée : {s.trade_date}</div>

          {/* Pré-market info — % et prix capturés depuis la liste Webull */}
          {(s.premarket?.percent != null || s.capture_price != null) && (
            <div
              className="mt-3 flex items-center gap-3 bg-input border border-line rounded-lg px-3 py-2"
              data-testid="stock-premarket-block"
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-2 shrink-0">Pré-market</div>
              <div className="flex items-center gap-4 flex-1 flex-wrap">
                {s.premarket?.percent != null && (
                  <div>
                    <div className="text-[9px] text-muted-2">%</div>
                    <div
                      className={`font-mono-num text-sm font-semibold ${s.premarket.percent >= 0 ? "text-invest" : "text-ignore"}`}
                      data-testid="stock-premarket-pct"
                    >
                      {s.premarket.percent >= 0 ? "+" : ""}{Number(s.premarket.percent).toFixed(2)} %
                    </div>
                  </div>
                )}
                {s.capture_price != null && (
                  <div>
                    <div className="text-[9px] text-muted-2">Valeur capturée</div>
                    <div className="font-mono-num text-sm text-app" data-testid="stock-premarket-value">
                      {Number(s.capture_price).toFixed(4).replace(/\.?0+$/, "")} $
                    </div>
                  </div>
                )}
                {s.premarket?.timestamp && (
                  <div className="text-[10px] font-mono-num text-muted-2 ml-auto">
                    à {new Date(s.premarket.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Real/Test toggle */}
          <div className="mt-5 flex items-center gap-3 bg-input border border-line rounded-lg p-3">
            <Switch
              checked={isReal}
              onCheckedChange={toggleStatus}
              data-testid="stock-status-toggle"
            />
            <div className="flex-1">
              <div className="text-sm font-medium">{isReal ? "Réel — a compté dans le capital" : "Test — simulation seulement"}</div>
              <div className="text-[11px] text-muted-1 mt-0.5">
                {isReal
                  ? "Ce titre alimente le capital réel, la réserve et le drawdown. Prix d'entrée et montant modifiables."
                  : "Prix d'entrée pré-rempli avec la capture, montant = allocation proposée. Ce titre n'affecte pas le capital réel."}
              </div>
            </div>
          </div>

          {/* Allocation + entry_amount */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-2">Allocation proposée</div>
              <div className="font-mono-num text-2xl mt-1" data-testid="stock-alloc-proposed">
                {formatMoney(s.proposed_allocation ?? 0)}
              </div>
              <div className="text-[10px] text-muted-2 mt-0.5">score {s.score} · poids {Math.max(0, s.score - 59)}</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-2">
                Montant {isReal ? "investi (Réel)" : "simulé (Test — auto)"}
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={amountDraft}
                  onChange={(e) => setAmountDraft(e.target.value)}
                  onBlur={saveAmount}
                  placeholder={formatMoney(s.effective_allocation ?? 0)}
                  disabled={!isReal}
                  className={`h-9 w-32 bg-input border-line-strong font-mono-num ${!isReal ? "opacity-60" : ""}`}
                  data-testid="stock-entry-amount"
                  inputMode="decimal"
                />
                <span className="text-muted-2 text-sm">$</span>
              </div>
              <div className="text-[10px] text-muted-2 mt-0.5" data-testid="stock-alloc-effective">
                Effectif : {formatMoney(s.effective_allocation ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Score breakdown */}
      <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6">
        <h2 className="font-display text-lg mb-4">Décomposition du score</h2>
        <div className="space-y-2">
          {Object.entries(br).map(([k, b]) => (
            <div key={k} className="grid grid-cols-[1fr_auto_100px] gap-4 items-center py-2 border-b border-line last:border-b-0">
              <div>
                <div className="text-sm">{b.label}</div>
                <div className="text-[11px] text-muted-2 font-mono-num">
                  {b.value === null || b.value === undefined ? "—" :
                    typeof b.value === "number" ? formatAbbrev(b.value) : String(b.value)}
                </div>
              </div>
              <div className="font-mono-num text-lg">{b.points}<span className="text-muted-2 text-xs">/{b.max}</span></div>
              <div className="h-1.5 bg-input rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${(b.points / b.max) * 100}%`, background: "var(--text)" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prices timeline (iter11) */}
      <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6">
        <h2 className="font-display text-lg mb-1">Prix du jour</h2>
        <p className="text-xs text-muted-1 mb-4">
          Le gain se calcule à partir des prix en dollars. Le pourcentage à côté est saisi
          manuellement (recopié depuis Webull au moment de la capture) — jamais recalculé.
        </p>
        <div className="space-y-3">
          <PriceRow
            title="0 · Prix d'ouverture $"
            hint={isReal ? "Prix d'ouverture du marché (info)" : "Base du calcul de gain Test (close − open) / open"}
            priceValue={s.open_price ?? s.criteria?.open_price}
            pctValue={null}
            testIdPrefix="mes-open"
            onSave={({ price }) => patch({ open_price: price }, "Prix d'ouverture mis à jour")}
          />
          <PriceRow
            title="1 · Prix d'entrée $"
            hint={isReal ? "Prix réel d'exécution + % Webull" : "Auto-rempli avec le prix capturé"}
            priceValue={s.entry_price}
            pctValue={s.entry?.percent}
            testIdPrefix="mes-entry"
            readOnly={!isReal}
            onSave={({ price, pct }) => patch({
              entry_price: price,
              entry: { percent: pct, timestamp: s.entry?.timestamp || new Date().toISOString() },
            }, "Entrée mise à jour")}
          />
          <PriceRow
            title="2 · Prix de sortie $ (optionnel)"
            hint="Si revendu avant la clôture — laisser vide sinon"
            priceValue={s.exit_price}
            pctValue={s.exit?.percent}
            testIdPrefix="mes-exit"
            onSave={({ price, pct }) => patch({
              exit_price: price,
              exit: { percent: pct, timestamp: s.exit?.timestamp || new Date().toISOString() },
            }, "Sortie mise à jour")}
          />
          <PriceRow
            title="3 · Prix de clôture $"
            hint="Référence de sortie si sortie non renseignée"
            priceValue={s.close_price}
            pctValue={s.close?.percent}
            testIdPrefix="mes-close"
            onSave={({ price, pct }) => patch({
              close_price: price,
              close: { percent: pct, timestamp: s.close?.timestamp || new Date().toISOString() },
            }, "Clôture mise à jour")}
          />
        </div>

        {/* Live gain preview — labeled realized vs unrealized */}
        {(() => {
          const realized = s.exit_price != null;
          const inProgress = !realized && s.close_price != null;
          const label = realized ? "Gain réalisé" : inProgress ? "Gain en cours" : "Position en cours";
          const amount = s.entry_price ? s.effective_allocation ?? null : null;
          const gainDollars = (gainPrices != null && amount != null)
            ? amount * (gainPrices / 100)
            : null;
          return (
            <div className="mt-4 flex items-center justify-between bg-input border border-line rounded-lg p-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-1">{label}</div>
                {realized && <div className="text-[10px] text-invest mt-0.5">Final — action vendue</div>}
                {inProgress && <div className="text-[10px] text-monitor mt-0.5">Non réalisé — action toujours détenue</div>}
              </div>
              <div className="text-right">
                <div
                  className={`font-mono-num text-2xl font-semibold ${gainPrices == null ? "text-muted-2" : gainPrices >= 0 ? "text-invest" : "text-ignore"}`}
                  data-testid="stock-gain-preview"
                >
                  {gainPrices == null ? "—" : formatPctInfo(gainPrices)}
                </div>
                {gainDollars != null && (
                  <div className={`text-xs font-mono-num mt-0.5 ${gainDollars >= 0 ? "text-invest" : "text-ignore"}`} data-testid="stock-gain-dollars">
                    {gainDollars >= 0 ? "+" : ""}{gainDollars.toFixed(2)} $
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Notes */}
      <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6">
        <h2 className="font-display text-lg mb-1">Notes / commentaires</h2>
        <p className="text-xs text-muted-1 mb-4">Contexte, catalyseurs, apprentissages… libre à vous.</p>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex : bougie avalante avant l'ouverture, gap sur volumes anormaux…"
          className="bg-input border-line-strong min-h-[120px]"
          data-testid="stock-notes-input"
        />
        <Button
          onClick={saveNotes}
          disabled={savingNotes}
          className="mt-3 btn-primary"
          data-testid="stock-notes-save"
        >{savingNotes ? "Enregistrement…" : "Enregistrer la note"}</Button>
      </div>

      {/* Delete */}
      <div className="flex justify-end">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="border-line-strong text-ignore hover:bg-ignore/10"
              data-testid="stock-delete-btn"
            ><Trash2 size={14} className="mr-2" /> Supprimer l'action</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer {s.symbol} ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est définitive. L'entrée sera retirée du journal et l'historique se recalculera automatiquement.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="stock-delete-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={del}
                className="bg-ignore text-white hover:bg-ignore/90"
                data-testid="stock-delete-confirm"
              >Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
